import type {
  AnswerRole,
  AppraisalDetail,
  AppraisalQuestion,
  AppraisalStatus,
} from "../services/appraisalService";

/**
 * Stage vocabulary and progress arithmetic for an appraisal.
 *
 * Pure functions with no React and no network, so the rules are testable
 * directly -- which matters because getting "which stage is this at, and what
 * can this person do" wrong is how a UI offers somebody a button that will be
 * refused.
 *
 * The server remains the authority: `available_actions` on the appraisal is
 * what decides which buttons appear. Nothing here re-derives permission.
 */

export const STAGE_LABELS: Record<AppraisalStatus, string> = {
  draft: "Draft",
  assigned: "Assigned",
  self_assessment: "Self-assessment",
  self_assessment_submitted: "Self-assessment submitted",
  supervisor_review: "Supervisor review",
  supervisor_completed: "Supervisor assessment complete",
  review_meeting: "Review meeting",
  development_planning: "Development planning",
  final_approval: "Final approval",
  awaiting_acknowledgement: "Awaiting acknowledgement",
  completed: "Completed",
  returned_for_revision: "Returned for revision",
};

export const ACTION_LABELS: Record<string, string> = {
  start_self_assessment: "Start self-assessment",
  submit_self_assessment: "Submit self-assessment",
  start_supervisor_review: "Begin supervisor review",
  complete_supervisor_review: "Complete assessment",
  record_review_meeting: "Record review meeting",
  complete_development_planning: "Complete development planning",
  submit_for_approval: "Send for approval",
  approve: "Approve",
  acknowledge: "Acknowledge",
  return_for_revision: "Return for revision",
  resume: "Resume",
};

/** Actions that need something more from the user before they can be sent. */
export const ACTIONS_NEEDING_INPUT = new Set([
  "record_review_meeting",
  "return_for_revision",
  "acknowledge",
]);

export function stageLabel(status: AppraisalStatus | string): string {
  return STAGE_LABELS[status as AppraisalStatus] ?? status;
}

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export type StatusTone = "neutral" | "progress" | "waiting" | "warning" | "done";

export function statusTone(status: AppraisalStatus): StatusTone {
  if (status === "completed") return "done";
  if (status === "returned_for_revision") return "warning";
  if (status === "draft" || status === "assigned") return "neutral";
  if (
    status === "final_approval" ||
    status === "awaiting_acknowledgement" ||
    status === "self_assessment_submitted" ||
    status === "supervisor_completed"
  ) {
    return "waiting";
  }
  return "progress";
}

/**
 * How far through its own path an appraisal is, 0-100.
 *
 * Measured against the stages THIS appraisal actually has, not a fixed list --
 * a support-staff form with no self-assessment and no development planning is
 * three-quarters done at a point where a teacher's form is only half done.
 */
export function progressPercent(
  stages: AppraisalStatus[],
  status: AppraisalStatus,
): number {
  if (!stages.length) return 0;
  if (status === "completed") return 100;
  // A returned appraisal has gone backwards; showing it as progress would
  // overstate where it is, so it reports against where it will resume.
  const index = stages.indexOf(status);
  if (index < 0) return 0;
  return Math.round((index / (stages.length - 1)) * 100);
}

export function nextStage(
  stages: AppraisalStatus[],
  status: AppraisalStatus,
): AppraisalStatus | null {
  const index = stages.indexOf(status);
  if (index < 0 || index + 1 >= stages.length) return null;
  return stages[index + 1];
}

/**
 * Which side of the form this user is filling in, or null if neither.
 *
 * Mirrors the server's own rule: being the appraisee or supervisor is not
 * enough on its own, the appraisal has to be at a stage where that side is
 * open. The server still enforces it; this just avoids rendering inputs that
 * would be refused on save.
 */
export function writableRole(
  appraisal: Pick<AppraisalDetail, "status" | "staff" | "supervisor">,
  currentUserId: number | undefined,
  appraiseeUserId: number | undefined,
): AnswerRole | null {
  if (!currentUserId) return null;
  const selfStages: AppraisalStatus[] = ["self_assessment", "returned_for_revision"];
  const supervisorStages: AppraisalStatus[] = [
    "supervisor_review",
    "review_meeting",
    "development_planning",
    "returned_for_revision",
  ];
  if (appraiseeUserId === currentUserId && selfStages.includes(appraisal.status)) {
    return "self";
  }
  if (appraisal.supervisor === currentUserId && supervisorStages.includes(appraisal.status)) {
    return "supervisor";
  }
  return null;
}

/** The questions one role answers, in form order. */
export function questionsForRole(
  form: { questions: AppraisalQuestion[] }[],
  role: AnswerRole,
): AppraisalQuestion[] {
  return form
    .flatMap((section) => section.questions)
    .filter((q) => q.answered_by === role || q.answered_by === "both");
}

/**
 * Whether a conditional question should be shown.
 *
 * The server computes the authoritative list (`visible_questions`), which the
 * form uses. This exists so the page can re-evaluate live as somebody types,
 * without a round trip per keystroke.
 */
export function isQuestionVisible(
  question: AppraisalQuestion,
  answersByQuestion: Record<number, { value: string }>,
): boolean {
  if (!question.conditional_on) return true;
  const parent = answersByQuestion[question.conditional_on];
  if (!parent) return false;
  return (
    parent.value.trim().toLowerCase() ===
    (question.conditional_value ?? "").trim().toLowerCase()
  );
}

/** Whether a question still needs an answer before its stage can be submitted. */
export function unansweredRequired(
  questions: AppraisalQuestion[],
  answersByQuestion: Record<number, { value: string }>,
  visibleIds: number[],
): AppraisalQuestion[] {
  const visible = new Set(visibleIds);
  return questions.filter((question) => {
    if (!question.is_required) return false;
    if (!visible.has(question.id)) return false;
    const answer = answersByQuestion[question.id];
    return !answer || !answer.value.trim();
  });
}

/**
 * The word for a rating, so a star or a number on the form reads as the same
 * judgement the finished appraisal will report.
 *
 * Mirrors `scoring.band_for_score` on the server, thresholds included, and is
 * pure for the same reason: on the default 1-5 scale each whole rating has to
 * land on its own label, and a 2 shown as "Unsatisfactory" when the record
 * will say "Needs improvement" is not a cosmetic difference.
 *
 *     rating  1     2     3     4     5
 *     ratio   0.0   0.25  0.5   0.75  1.0
 */
export function ratingBandLabel(
  value: number,
  scaleMin: number,
  scaleMax: number,
): string {
  if (!Number.isFinite(value) || scaleMax <= scaleMin) return "";
  const ratio = (value - scaleMin) / (scaleMax - scaleMin);
  if (ratio >= 0.9) return "Outstanding";
  if (ratio >= 0.7) return "Exceeds expectations";
  if (ratio >= 0.45) return "Meets expectations";
  if (ratio >= 0.2) return "Needs improvement";
  return "Unsatisfactory";
}

/** How many of a role's visible questions have an answer, for section progress. */
export function answeredCount(
  questions: AppraisalQuestion[],
  answersByQuestion: Record<number, { value: string }>,
): number {
  return questions.filter((q) => (answersByQuestion[q.id]?.value ?? "").trim() !== "")
    .length;
}
